/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * Central Model Registry
 * Single source of truth for available AI models (Image & Video).
 */

export type ModelProvider = 'gemini' | 'fal' | 'huggingface' | 'replicate' | 'openai';

export interface ModelDefinition {
    id: string;             // Unique ID (e.g., 'nano_banana_pro', 'flux_1.1')
    label: string;          // UI Label
    provider: ModelProvider;
    type: 'image' | 'video' | 'audio';

    // Technical configs
    endpoint?: string;      // API Endpoint path or Fal model ID (e.g. 'fal-ai/flux-pro/v1.1')
    description?: string;   // Tooltip info

    // Capabilities
    supportsReferenceImages?: boolean; // Can it take character refs?
    requiresFalKey?: boolean;
}

export const AVAILABLE_MODELS: ModelDefinition[] = [
    // === IMAGE MODELS ===
    {
        id: 'nano_banana_pro',
        label: '⚡ Nano Banana Pro (Gemini)',
        provider: 'gemini',
        type: 'image',
        description: 'Native rapid prototyping model. Best for iterating layouts.',
        supportsReferenceImages: true
    },
    {
        id: 'flux_pro_v1.1',
        label: '🎨 Flux Pro v1.1 (Fal)',
        provider: 'fal',
        type: 'image',
        endpoint: 'fal-ai/flux-pro/v1.1',
        description: 'SOTA open weights model via Fal.ai. Best for photorealism.',
        requiresFalKey: true,
        supportsReferenceImages: false // Base Flux usually needs LoRA or IP-Adapter, standard endpoint might not
    },
    {
        id: 'flux_realism',
        label: '📸 Flux Realism (Fal)',
        provider: 'fal',
        type: 'image',
        endpoint: 'fal-ai/flux-realism',
        description: 'Fine-tuned Flux for extreme realism.',
        requiresFalKey: true
    },
    {
        id: 'ideogram_v2',
        label: '✨ Ideogram V2 (Fal)',
        provider: 'fal',
        type: 'image',
        endpoint: 'fal-ai/ideogram/v2',
        description: 'Excellent text rendering and creative styles.',
        requiresFalKey: true
    },
    {
        id: 'kling_image',
        label: '🖼️ Kling Image (Replicate)',
        provider: 'replicate',
        type: 'image',
        endpoint: 'kwaivgi/kolors',
        description: 'Kling/Kolors image generation by Kuaishou.',
        requiresFalKey: false
    },

    // === VIDEO MODELS ===
    {
        id: 'veo_3.1',
        label: '🎥 Veo 3.1 (Google)',
        provider: 'gemini',
        type: 'video',
        description: 'Latest Veo model. Superior quality and coherence.',
        supportsReferenceImages: true
    },
    {
        id: 'veo',
        label: '🎥 Veo (Google)',
        provider: 'gemini',
        type: 'video',
        description: 'High fidelity video generation.',
        supportsReferenceImages: true
    },
    {
        id: 'veo_fast',
        label: '⚡ Veo Fast (Google)',
        provider: 'gemini',
        type: 'video',
        description: 'Low latency video generation (max 720p).',
        supportsReferenceImages: true
    },
    {
        id: 'ltx_video',
        label: '🎞️ LTX-2 Video (Fal)',
        provider: 'fal',
        type: 'video',
        endpoint: 'fal-ai/ltx-video',
        description: 'Fast and coherent video generation.',
        requiresFalKey: true
    },
    {
        id: 'ltx_audio',
        label: '🎵 LTX Audio-to-Video (Fal)',
        provider: 'fal',
        type: 'video',
        endpoint: 'fal-ai/ltx-video/audio',
        description: 'Generate video synced to audio input.',
        requiresFalKey: true
    },
    // === WAN MODELS (Fal) ===
    {
        id: 'wan_2.1',
        label: '🎬 Wan 2.1 (Fal)',
        provider: 'fal',
        type: 'video',
        endpoint: 'fal-ai/wan/v2.1',
        description: 'Wan video model v2.1.',
        requiresFalKey: true
    },
    {
        id: 'wan_2.6_1.3b',
        label: '🎬 Wan 2.6 1.3B (Fal)',
        provider: 'fal',
        type: 'video',
        endpoint: 'fal-ai/wan/v2.6-1.3b',
        description: 'Wan 2.6 lightweight 1.3B variant.',
        requiresFalKey: true
    },
    {
        id: 'wan_2.6_14b',
        label: '🎬 Wan 2.6 14B (Fal)',
        provider: 'fal',
        type: 'video',
        endpoint: 'fal-ai/wan/v2.6-14b',
        description: 'Wan 2.6 full 14B model. Highest quality.',
        requiresFalKey: true
    },
    // === KLING (Replicate) ===
    {
        id: 'kling_v1.6',
        label: '🎥 Kling 1.6 Pro (Replicate)',
        provider: 'replicate',
        type: 'video',
        endpoint: 'kwaivgi/kling-v1.6-pro',
        description: 'Kling 1.6 Pro by Kuaishou. Excellent motion.',
        requiresFalKey: false // Uses Replicate key instead
    }
];

export const getModelById = (id: string) => AVAILABLE_MODELS.find(m => m.id === id);

export const getImageModels = () => AVAILABLE_MODELS.filter(m => m.type === 'image');
export const getVideoModels = () => AVAILABLE_MODELS.filter(m => m.type === 'video');
